import React from 'react';

import {
  Box,
  Button,
  IconButton,
  styled,
  Typography
} from '@mui/material';
import {
  BaseModal,
  fields,
  Form,
  formUtil
} from 'iron-forge-react-modules';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';

import { Close } from '@mui/icons-material';
import * as dataService from './dataService';
import * as types from './types';



const TextFieldController = fields.TextField.genControllerComponent<types.IFormState>();
const CheckBoxField = fields.CheckboxField.genControllerComponent<types.IFormState>();



const FormContainerBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2)
}));

const ButtonBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between'
}));

const OverrideSkuFieldsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1)
}));


export interface IProps {
  leaseApplicationId: string;
  orderId: string;
  onClose: () => void;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.orderId);
  const [fnMutate, tuple] = dataService.useOverrideLeaseApplication(props.orderId);



  const form = useForm<types.IFormState>({
    defaultValues: {
      applicationNumber: '',
      scannedSkus: []
    }
  });

  const skuArray = useFieldArray<types.IFormState>({
    control: form.control,
    name: 'scannedSkus'
  });



  formUtil.useSetFormFields(queryTuple, form.setValue, ['applicationNumber', 'scannedSkus']);



  async function handleSubmit(data: types.IFormState) {
    await fnMutate(data, props.leaseApplicationId);
    props.onClose();
  }



  const content = (
    <Form.FC onSubmit={form.handleSubmit(handleSubmit)}>
      <FormContainerBox>
        <TextFieldController
          isRequired
          isDisabled={tuple.loading}
          fieldKey="applicationNumber"
          formControl={form.control}
          label="Application Number" />

        {skuArray.fields.map((field, index) => (
          <OverrideSkuFieldsContainer key={field.id}>
            <TextFieldController
              label="Scanned SKU"
              fieldKey={`scannedSkus.${index}.scannedValue`}
              formControl={form.control}
              isDisabled />

            <TextFieldController
              label="SKU to send"
              fieldKey={`scannedSkus.${index}.newValue`}
              formControl={form.control} />

            <Box>
              <CheckBoxField
                label="Send to SmartPay"
                fieldKey={`scannedSkus.${index}.shouldSend`}
                formControl={form.control} />
            </Box>
          </OverrideSkuFieldsContainer>
        ))}

        <Typography>
          You understand that by overriding the application number, you are potentially originating and activating a lease *not* associated with this order. If you are here, you know what you are doing and why you are doing it.
        </Typography>

        <ButtonBox>
          <Button
            disabled={tuple.loading}
            onClick={props.onClose}>
            Go Back
          </Button>

          <Button
            disabled={tuple.loading}
            type="submit"
            variant="contained">
            Submit
          </Button>
        </ButtonBox>
      </FormContainerBox>
    </Form.FC>
  );


  const fnCloseButton: BaseModal.IProps['fnGenElTopCloseButton'] = ({ onClose }) => (
    <IconButton onClick={onClose}>
      <Close />
    </IconButton>
  );



  return (
    <BaseModal.FC
      isOpen
      isDoubleWide
      fnGenElTopCloseButton={fnCloseButton}
      fnGenElCloseButton={fnCloseButton}>
      {() => content}
    </BaseModal.FC>
  );
};
